import { Box, IconButton, Typography } from '@mui/material';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import { FeedbackType, PostCallFeedbackState } from './types';
import FeedbackActions from './FeedbackActions';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';
import { useTracking } from '../../../tracking';

interface AccuracyQuestionProps {
  feedbackType: FeedbackType;
  postCallFeedback: PostCallFeedbackState;
  isUpdatingPostCallFeedback: boolean;
  setPostCallFeedback: (postCallFeedback: PostCallFeedbackState) => void;
  onClose: () => void;
  submitFeedback: (feedback?: PostCallFeedbackState) => Promise<void>;
}

const AccuracyQuestion = ({
  feedbackType,
  postCallFeedback,
  isUpdatingPostCallFeedback,
  setPostCallFeedback,
  onClose,
  submitFeedback,
}: AccuracyQuestionProps) => {
  const callDataQualityFF = useFeatureFlag('call-data-quality');
  const tracking = useTracking();
  const { generateTrackingProps } = tracking || {};

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        ...(!callDataQualityFF && { gap: '16px', width: '100%' }),
        ...(callDataQualityFF && {
          marginLeft: 'auto',
          width: 'fit-content',
        }),
      }}
    >
      <Typography sx={{ ...(callDataQualityFF && { marginRight: 2 }) }}>
        {feedbackType === 'summary'
          ? 'Was this summary accurate?'
          : 'Was this transcription accurate?'}
      </Typography>

      {!isUpdatingPostCallFeedback && (
        <IconButton
          {...(generateTrackingProps
            ? generateTrackingProps('thumbs-up-button')
            : {})}
          onClick={async () => {
            const updatedFeedback = {
              ...postCallFeedback,
              [feedbackType === 'summary'
                ? 'isAccurateSummary'
                : 'isAccurateTranscription']: true,
            };
            setPostCallFeedback(updatedFeedback);

            if (callDataQualityFF) {
              await submitFeedback(updatedFeedback);
            }
          }}
          disabled={
            isUpdatingPostCallFeedback ||
            postCallFeedback[
              feedbackType === 'summary'
                ? 'isAccurateSummary'
                : 'isAccurateTranscription'
            ]
          }
        >
          <ThumbUpAltOutlinedIcon
            color={
              postCallFeedback[
                feedbackType === 'summary'
                  ? 'isAccurateSummary'
                  : 'isAccurateTranscription'
              ]
                ? 'success'
                : 'info'
            }
          />
        </IconButton>
      )}
      {!isUpdatingPostCallFeedback && (
        <IconButton
          {...(generateTrackingProps
            ? generateTrackingProps('thumbs-down-button')
            : {})}
          onClick={() =>
            setPostCallFeedback({
              ...postCallFeedback,
              [feedbackType === 'summary'
                ? 'isAccurateSummary'
                : 'isAccurateTranscription']: false,
            })
          }
          disabled={
            isUpdatingPostCallFeedback ||
            postCallFeedback[
              feedbackType === 'summary'
                ? 'isAccurateSummary'
                : 'isAccurateTranscription'
            ] === false
          }
        >
          <ThumbDownAltOutlinedIcon
            color={
              postCallFeedback[
                feedbackType === 'summary'
                  ? 'isAccurateSummary'
                  : 'isAccurateTranscription'
              ] === false
                ? 'error'
                : 'info'
            }
          />
        </IconButton>
      )}

      {!callDataQualityFF && (
        <Box sx={{ marginLeft: 'auto' }}>
          <FeedbackActions
            feedbackType={feedbackType}
            onClose={onClose}
            submitFeedback={submitFeedback}
            isUpdatingPostCallFeedback={isUpdatingPostCallFeedback}
            postCallFeedback={postCallFeedback}
          />
        </Box>
      )}
    </Box>
  );
};

export default AccuracyQuestion;
